import { useEffect, useState, useRef, useLayoutEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { PATHS } from '../../router/paths';
import HeaderActions from './HeaderActions';

export default function PageNavbar() {
  const { t } = useTranslation();
  const location = useLocation();
  const [isMobileViewport, setIsMobileViewport] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const isActive = (path: string) => location.pathname === path;

  // Refs for direct DOM manipulation — bypasses React re-renders on scroll
  const navRef = useRef<HTMLElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const rowRef = useRef<HTMLDivElement>(null);
  const logoContainerRef = useRef<HTMLDivElement>(null);
  const logoIconRef = useRef<HTMLElement>(null);
  const brandTextRef = useRef<HTMLSpanElement>(null);
  const desktopNavRef = useRef<HTMLDivElement>(null);
  const headerActionsWrapperRef = useRef<HTMLDivElement>(null);
  const isMobileRef = useRef(false);

  useEffect(() => {
    isMobileRef.current = isMobileViewport;
  }, [isMobileViewport]);

  const applyScrollProgress = (p: number) => {
    const m = isMobileRef.current;

    if (navRef.current) {
      navRef.current.style.paddingTop = `${(m ? 12 : 26) * (1 - p)}px`;
    }
    if (containerRef.current) {
      const topR = (m ? 22 : 28) * (1 - p);
      const botR = (m ? 22 : 28) - p * (m ? 2 : 4);
      containerRef.current.style.backgroundColor = `rgba(13,13,13,${0.66 + p * 0.08})`;
      containerRef.current.style.borderTopLeftRadius = `${topR}px`;
      containerRef.current.style.borderTopRightRadius = `${topR}px`;
      containerRef.current.style.borderBottomLeftRadius = `${botR}px`;
      containerRef.current.style.borderBottomRightRadius = `${botR}px`;
    }
    if (rowRef.current) {
      const pad = (m ? 12 : 18) - p * (m ? 6 : 12);
      rowRef.current.style.paddingTop = `${pad}px`;
      rowRef.current.style.paddingBottom = `${pad}px`;
    }
    if (logoContainerRef.current) {
      const sz = (m ? 42 : 60) - p * (m ? 8 : 24);
      const r = (m ? 16 : 22) - p * (m ? 4 : 10);
      logoContainerRef.current.style.width = `${sz}px`;
      logoContainerRef.current.style.height = `${sz}px`;
      logoContainerRef.current.style.borderRadius = `${r}px`;
    }
    if (logoIconRef.current) {
      logoIconRef.current.style.fontSize = `${(m ? 20 : 30) - p * (m ? 2 : 5)}px`;
    }
    if (brandTextRef.current) {
      brandTextRef.current.style.fontSize = `${(m ? 17 : 25) - p * (m ? 1.5 : 5)}px`;
    }
    const scale = 1.1 - p * 0.18;
    if (desktopNavRef.current) {
      desktopNavRef.current.style.transform = `scale(${scale})`;
    }
    if (headerActionsWrapperRef.current) {
      headerActionsWrapperRef.current.style.transform = `scale(${scale})`;
      headerActionsWrapperRef.current.style.transformOrigin = 'right center';
    }
  };

  useLayoutEffect(() => {
    applyScrollProgress(Math.min(window.scrollY / 110, 1));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    applyScrollProgress(Math.min(window.scrollY / 110, 1));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMobileViewport]);

  useEffect(() => {
    let frameId = 0;
    const handleScroll = () => {
      cancelAnimationFrame(frameId);
      frameId = window.requestAnimationFrame(() => {
        applyScrollProgress(Math.min(window.scrollY / 110, 1));
      });
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      cancelAnimationFrame(frameId);
      window.removeEventListener('scroll', handleScroll);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 1199px)');

    const syncViewport = () => {
      const mobile = mediaQuery.matches;
      setIsMobileViewport(mobile);

      if (!mobile) {
        setIsMobileMenuOpen(false);
      }
    };

    syncViewport();
    mediaQuery.addEventListener('change', syncViewport);

    return () => {
      mediaQuery.removeEventListener('change', syncViewport);
    };
  }, []);

  useEffect(() => {
    if (!isMobileViewport || !isMobileMenuOpen) {
      return;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isMobileMenuOpen, isMobileViewport]);

  const navItems = [
    { to: PATHS.home, label: t('nav.home'), active: isActive(PATHS.home) },
    { to: PATHS.homeSluzby, label: t('nav.services'), active: false },
    { to: PATHS.cennik, label: t('nav.pricing'), active: isActive(PATHS.cennik) },
    { to: PATHS.omne, label: t('nav.about'), active: isActive(PATHS.omne) },
    { to: PATHS.kontakt, label: t('nav.contact'), active: isActive(PATHS.kontakt) },
  ];

  const navbar = (
    <nav
      ref={navRef}
      className="fixed inset-x-0 top-0 z-50 px-4 sm:px-6"
      style={{ transition: 'padding-top 160ms cubic-bezier(0.22, 1, 0.36, 1)' }}
    >
      <div
        ref={containerRef}
        className="relative mx-auto max-w-7xl overflow-hidden border border-white/[0.12] px-4 shadow-[0_24px_70px_rgba(0,0,0,0.48),inset_0_1px_0_rgba(255,255,255,0.08)] backdrop-blur-[28px] backdrop-saturate-150 sm:px-6"
      >
        <div
          ref={rowRef}
          className="flex items-center justify-between gap-4"
        >
          {/* Logo */}
          <Link to="/" className="flex min-w-0 items-center gap-2 sm:gap-3 group cursor-pointer">
            <div
              ref={logoContainerRef}
              className="relative flex items-center justify-center border border-brand-green/25 bg-brand-green/15"
            >
              <i ref={logoIconRef} className="ri-line-chart-line text-brand-green"></i>
            </div>
            <span
              ref={brandTextRef}
              className="font-caprasimo text-white group-hover:text-brand-green transition-colors whitespace-nowrap"
              style={{ lineHeight: 1 }}
            >
              {t('nav.brand')}
            </span>
          </Link>

          {/* Navigation Links */}
          <div ref={desktopNavRef} className="hidden min-[1200px]:flex items-center gap-2 rounded-full bg-black/10 px-3 py-2" style={{ transformOrigin: 'center' }}>
            {navItems.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className={`rounded-full px-4 py-2 text-sm transition-all cursor-pointer whitespace-nowrap ${
                  item.active
                    ? 'bg-white/12 font-semibold text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.06)]'
                    : 'font-normal text-white/76 hover:bg-white/8 hover:text-white'
                }`}
              >
                {item.label}
              </Link>
            ))}
          </div>

          <div ref={headerActionsWrapperRef} className="hidden min-[1200px]:block">
            <HeaderActions scale={1} />
          </div>

          <button
            type="button"
            onClick={() => setIsMobileMenuOpen((current) => !current)}
            className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-white/10 bg-white/[0.05] text-white transition-all hover:bg-white/[0.08] min-[1200px]:hidden"
            aria-label={isMobileMenuOpen ? t('modal.close') : t('nav.menu')}
            aria-expanded={isMobileMenuOpen}
          >
            <i className={`${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-3-line'} text-[1.15rem]`} />
          </button>
        </div>

        {isMobileViewport && isMobileMenuOpen ? (
          <div className="border-t border-white/10 pb-5 pt-4 min-[1200px]:hidden">
            <div className="flex flex-col gap-2">
              {navItems.map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`rounded-2xl border px-4 py-3 text-sm font-semibold transition-colors ${
                    item.active
                      ? 'border-brand-green/24 bg-brand-green/[0.1] text-white'
                      : 'border-white/10 bg-white/[0.04] text-white/84 hover:bg-white/[0.07] hover:text-white'
                  }`}
                >
                  {item.label}
                </Link>
              ))}
            </div>

            <div className="mt-4">
              <HeaderActions
                scale={1}
                orientation="column"
                fullWidth
                className="w-full"
              />
            </div>
          </div>
        ) : null}
      </div>
    </nav>
  );

  return createPortal(navbar, document.body);
}
